import type { ReactElement } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';

export interface ProcessDetailsData {
  pid: number;
  isRoot: boolean;
  binaryPath: string | null;
  commandLine: string | null;
  parentName: string | null;
  ppid?: number | null;
}

export function ProcessDetails({
  process,
  children,
}: {
  process: ProcessDetailsData;
  children?: ReactElement | null;
}): ReactElement {
  return (
    <div className="border-l-2 border-muted-foreground/25 pl-3 space-y-0.5 text-xs">
      <div className="font-mono text-muted-foreground">
        PID {process.pid}
        {process.isRoot && (
          <Badge variant="destructive" className="text-xs px-1 py-0 ml-2">
            root
          </Badge>
        )}
      </div>
      {process.binaryPath && (
        <div className="flex items-baseline gap-1 min-w-0">
          <span className="text-muted-foreground shrink-0">Binary:</span>
          <code className="bg-muted px-1 py-0.5 rounded truncate block">
            {process.binaryPath}
          </code>
        </div>
      )}
      {process.commandLine && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-baseline gap-1 min-w-0 cursor-default">
              <span className="text-muted-foreground shrink-0">Command:</span>
              <code className="bg-muted px-1 py-0.5 rounded truncate block">
                {process.commandLine}
              </code>
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="max-w-lg break-all font-mono"
          >
            {process.commandLine}
          </TooltipContent>
        </Tooltip>
      )}
      {process.parentName && (
        <div className="flex items-baseline gap-1 min-w-0">
          <span className="text-muted-foreground shrink-0">Parent:</span>
          <span className="font-mono truncate">
            {process.parentName}
            {process.ppid != null && ` (PID ${process.ppid})`}
          </span>
        </div>
      )}
      {children}
    </div>
  );
}
